import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { AppDispatch, RootState } from 'store'
import { getFoodExceptions, resolveFoodException } from 'features/food/foodSlice'
import {
  FoodExceptionResolutionActionType,
  FoodExceptionSeverity,
  FoodExceptionStatus,
  IFoodExceptionRecordResponse,
} from 'types/api/foodApi.interface'
import { formatDateForDisplay } from 'utils/helpers'
import { toast } from 'react-toastify'
import { ISelectValue } from 'constants/globalTypes'

const exceptionTypeLabels = [
  '',
  'Dietary mismatch',
  'Missing kitchen confirmation',
  'Profile conflict',
  'Unresolved override note',
  'Standby insufficient',
  'Consumption posting failed',
  'Meal window without plan',
]

const severityLabel = (severity: number) => {
  if (severity === FoodExceptionSeverity.Critical) return 'Critical'
  if (severity === FoodExceptionSeverity.High) return 'High'
  if (severity === FoodExceptionSeverity.Medium) return 'Medium'
  return 'Low'
}

const severityPill = (severity: number) => {
  if (severity === FoodExceptionSeverity.Critical || severity === FoodExceptionSeverity.High)
    return 'danger'
  if (severity === FoodExceptionSeverity.Medium) return 'accent'
  return 'neutral'
}

const statusLabel = (status: number) => {
  if (status === FoodExceptionStatus.Resolved) return 'Resolved'
  if (status === FoodExceptionStatus.AutoClosed) return 'Auto-closed'
  return 'Open'
}

const resolutionActionOptions = [
  { label: 'Reassign kitchen', value: FoodExceptionResolutionActionType.ReassignKitchen },
  { label: 'Assign diet-safe standby', value: FoodExceptionResolutionActionType.AssignDietSafeStandby },
  { label: 'Cancel meal intent', value: FoodExceptionResolutionActionType.CancelMealIntent },
  { label: 'Confirm on behalf', value: FoodExceptionResolutionActionType.ConfirmOnBehalf },
  { label: 'Correct profile', value: FoodExceptionResolutionActionType.CorrectProfile },
  { label: 'Comment', value: FoodExceptionResolutionActionType.Comment },
  { label: 'Retry consumption posting', value: FoodExceptionResolutionActionType.RetryConsumptionPosting },
  { label: 'Adjust plan', value: FoodExceptionResolutionActionType.AdjustPlan },
]

const legalActionOptions = (legalActions?: number[]) => {
  if (!legalActions?.length) return []
  return resolutionActionOptions.filter((o) => legalActions.includes(o.value))
}

const statusFilterOptions = [
  { label: 'All', value: 0 },
  { label: 'Open', value: FoodExceptionStatus.Open },
  { label: 'Resolved', value: FoodExceptionStatus.Resolved },
  { label: 'Auto-closed', value: FoodExceptionStatus.AutoClosed },
]

export const ExceptionsTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { exceptions, isLoading } = useSelector((store: RootState) => store.food)
  const [statusFilter, setStatusFilter] = useState(FoodExceptionStatus.Open)
  const [selected, setSelected] = useState<IFoodExceptionRecordResponse | null>(null)
  const [actionType, setActionType] = useState(FoodExceptionResolutionActionType.Comment)
  const [comment, setComment] = useState('')

  const refresh = () => {
    dispatch(getFoodExceptions({ page: 1, limit: 50, status: statusFilter || undefined }))
  }

  useEffect(() => {
    refresh()
  }, [dispatch, statusFilter])

  const handleResolve = async () => {
    if (!selected) return
    if (actionType === FoodExceptionResolutionActionType.Comment && !comment.trim()) {
      toast.warn('A comment is required for the Comment action')
      return
    }
    const resp = await dispatch(
      resolveFoodException({
        id: selected.id,
        body: { actionType, comment: comment.trim() || null },
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      setSelected(null)
      setComment('')
      refresh()
    }
  }

  const columns: IHonestTableColumn<IFoodExceptionRecordResponse>[] = [
    {
      id: 'severity',
      header: 'Severity',
      cell: (row) => (
        <StatusPill status={severityPill(row.severity)}>{severityLabel(row.severity)}</StatusPill>
      ),
    },
    { id: 'type', header: 'Type', cell: (row) => exceptionTypeLabels[row.type] || `Type ${row.type}` },
    {
      id: 'operationalDay',
      header: 'Day',
      cell: (row) => formatDateForDisplay(row.operationalDay),
    },
    { id: 'windowDef', header: 'Window', cell: (row) => row.windowDef?.name || '—' },
    { id: 'kitchen', header: 'Kitchen', cell: (row) => row.kitchen?.name || '—' },
    {
      id: 'person',
      header: 'Person',
      cell: (row) => (row.person ? `${row.person.firstName} ${row.person.lastName}` : '—'),
    },
    { id: 'rootCause', header: 'Root cause', minWidth: '18rem', cell: (row) => row.rootCause },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => (
        <StatusPill status={row.status === FoodExceptionStatus.Open ? 'accent' : 'success'}>
          {statusLabel(row.status)}
        </StatusPill>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        row.status === FoodExceptionStatus.Open ? (
          <Button
            size='sm'
            variant='secondary'
            onClick={() => {
              setSelected(row)
              const legal = legalActionOptions(row.legalActions)
              setActionType(legal[0]?.value ?? FoodExceptionResolutionActionType.Comment)
              setComment('')
            }}
          >
            Resolve
          </Button>
        ) : (
          <OtherText grey>{row.resolutionComment || ''}</OtherText>
        ),
    },
  ]

  return (
    <Container>
      <ContainerHeader padding>
        <Flex between center width='100%' wrap gap='0.75rem'>
          <TitleText weight='700'>Exceptions</TitleText>
          <HonestSelectInput
            nomarg
            labelText='Status'
            options={statusFilterOptions}
            defaultValue={statusFilter}
            onChange={(v: ISelectValue) => setStatusFilter(Number(v.value))}
            name='exceptionStatus'
          />
        </Flex>
      </ContainerHeader>
      <ContainerContent>
        <HonestTable
          columns={columns}
          data={exceptions.data || []}
          keyField='id'
          loading={isLoading}
          emptyMessage='No exceptions for this filter'
        />
      </ContainerContent>
      {selected && (
        <Modal black>
          <Flex column gap='1rem'>
            <Flex between width='100%' center>
              <TitleText weight='bold'>Resolve Exception #{selected.id}</TitleText>
              <CursorPointer onClick={() => setSelected(null)}>
                <CloseIcon />
              </CursorPointer>
            </Flex>
            <BodyText>{selected.rootCause}</BodyText>
            <OtherText grey>
              Legal actions depend on the exception type — the backend rejects anything else.
              Actions like cancelling a meal intent are permanent.
            </OtherText>
            <HonestSelectInput
              labelText='Action'
              options={legalActionOptions(selected.legalActions)}
              defaultValue={actionType}
              onChange={(v: ISelectValue) => setActionType(Number(v.value))}
              name='resolutionAction'
            />
            {!legalActionOptions(selected.legalActions).length && (
              <OtherText grey>No legal actions available for this exception.</OtherText>
            )}
            <Input
              labelText='Comment'
              value={comment}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setComment(e.target.value)}
              placeholder='Kitchen confirmed by phone...'
              nomarg
            />
            <Flex width='100%' center justcenter>
              <Button
                variant='primary'
                disabled={!legalActionOptions(selected.legalActions).length}
                onClick={handleResolve}
              >
                Resolve
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
    </Container>
  )
}
